import React, { useState, useRef, useMemo } from 'react';
import {
  array,
  arrayOf,
  bool,
  func,
  shape,
  string,
  oneOf,
  object,
} from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import LISTING_STATUS from '../../util/enums/LISTING_STATUS.js';
import SimilarProducts from './SimilarProducts';
import {
  requestUpdateListingStatus,
  requestUpdeteListingStatus,
  loadData,
  sendSellerMessage,
  markSellerMessagesRead,
} from './ListingPage.duck';
import { apiBaseUrl } from '../../util/api';
import { trackProductView } from '../../util/gtmUtils';

// Contexts
import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
// Utils
import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import {
  LISTING_STATE_PENDING_APPROVAL,
  LISTING_STATE_CLOSED,
  propTypes,
} from '../../util/types';
import { types as sdkTypes } from '../../util/sdkLoader';
import { formatMoney } from '../../util/currency';

import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_PARAM_TYPE_DRAFT,
  LISTING_PAGE_PARAM_TYPE_EDIT,
  createSlug,
  NO_ACCESS_PAGE_USER_PENDING_APPROVAL,
} from '../../util/urlHelpers';
import {
  isErrorUserPendingApproval,
  isForbiddenError,
} from '../../util/errors.js';
import { isUserAuthorized } from '../../util/userHelpers.js';
import { convertMoneyToNumber } from '../../util/currency';
import {
  ensureListing,
  ensureOwnListing,
  ensureUser,
  userDisplayNameAsString,
} from '../../util/data';
import { richText } from '../../util/richText';
import {
  isBookingProcess,
  isPurchaseProcess,
  resolveLatestProcessName,
} from '../../transactions/transaction';

// Global ducks (for Redux actions and thunks)
import { getMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import {
  manageDisableScrolling,
  isScrollingDisabled,
} from '../../ducks/ui.duck';
import { initializeCardPaymentData } from '../../ducks/stripe.duck.js';
import AccordionGroup from '../../components/Accordion/AccordionGroup';
import ConditionBox from '../../components/ConditionBox/ConditionBox.js';
import { ConditionToLabel } from '../../util/enums/CONDITION.js';

// Shared components
import {
  Breadcrumb,
  H4,
  Page,
  Button,
  NamedLink,
  NamedRedirect,
  OrderPanel,
  LayoutSingleColumn,
  PrimaryButtonInline,
  SellerChat,
  SellerMessagesDrawer,
} from '../../components';

// Related components and modules
import TopbarSpacer from '../TopbarContainer/TopbarSpacer';
import FooterContainer from '../FooterContainer/FooterContainer';
import NotFoundPage from '../NotFoundPage/NotFoundPage';

import {
  sendInquiry,
  setInitialValues,
  fetchTimeSlots,
  fetchTransactionLineItems,
} from './ListingPage.duck';

import {
  LoadingPage,
  ErrorPage,
  priceData,
  listingImages,
  handleContactUser,
  handleSubmitInquiry,
  handleSubmit,
  handleToggleFavorites,
} from './ListingPage.shared';

import { updateProfile } from '../ProfileSettingsPage/ProfileSettingsPage.duck';
import ActionBarMaybe from './ActionBarMaybe';
import SectionGallery from './SectionGallery';
import CustomListingFields from './CustomListingFields';

import css from './ListingPage.module.css';
import { useEffect } from 'react';
import { requestUpdateListing } from '../EditListingPage/EditListingPage.duck.js';

const MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE = 16;

const { UUID } = sdkTypes;

const buildBreadcrumbItems = (locationState, listingTitle) => {
  const fromSearch = locationState?.fromSearch;
  if (fromSearch) {
    const params = new URLSearchParams(fromSearch);
    const category = params.get('pub_categoryLevel1');
    if (category) {
      const categoryLabel = category.charAt(0).toUpperCase() + category.slice(1);
      return [
        { label: 'All Listings', name: 'SearchPage' },
        { label: categoryLabel, href: `/s${fromSearch}` },
        { label: listingTitle },
      ];
    }
  }
  return [
    { label: 'All Listings', name: 'SearchPage' },
    { label: listingTitle },
  ];
};

export const ListingPageComponent = props => {
  const [inquiryModalOpen, setInquiryModalOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Initialize inquiryModalOpen state based on props
  useEffect(() => {
    const shouldOpen = props.inquiryModalOpenForListingId === props.params.id;
    setInquiryModalOpen(shouldOpen);
  }, [props.inquiryModalOpenForListingId, props.params.id]);

  const {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    intl,
    onManageDisableScrolling,
    params: rawParams,
    location,
    scrollingDisabled,
    showListingInProgress,
    showListingError,
    monthlyTimeSlots,
    onFetchTimeSlots,
    onFetchTransactionLineItems,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    history,
    callSetInitialValues,
    onSendInquiry,
    onInitializeCardPaymentData,
    config,
    routeConfiguration,
    onUpdateFavorites,
    onSendSellerMessage,
    sendSellerMessageInProgress,
    onMarkSellerMessagesRead,
  } = props;

  const listingId = new UUID(rawParams.id);
  const isPendingApprovalVariant =
    rawParams.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT;
  const isDraftVariant = rawParams.variant === LISTING_PAGE_DRAFT_VARIANT;

  // Get raw listing data first — for live listings, fall back to ownListing if
  // the listing was fetched via sdk.ownListings.show (logged-in owner case)
  const rawListing =
    isPendingApprovalVariant || isDraftVariant
      ? getOwnListing(listingId)
      : getListing(listingId) || getOwnListing(listingId);

  // Memoize the ensured listing based on listing ID and raw listing data
  const currentListing = useMemo(() => {
    const listing =
      isPendingApprovalVariant || isDraftVariant
        ? ensureOwnListing(getOwnListing(listingId))
        : ensureListing(getListing(listingId) || getOwnListing(listingId));

    return listing;
  }, [
    listingId.uuid,
    isPendingApprovalVariant,
    isDraftVariant,
    rawListing?.id?.uuid,
    rawListing?.attributes?.title,
    rawListing?.images?.length,
    getListing,
    getOwnListing,
  ]);

  const listingSlug =
    rawParams.slug || createSlug(currentListing.attributes.title || '');
  const params = { slug: listingSlug, ...rawParams };

  const listingPathParamType = isDraftVariant
    ? LISTING_PAGE_PARAM_TYPE_DRAFT
    : LISTING_PAGE_PARAM_TYPE_EDIT;
  // Accordion data cached per listing ID — no stale data on navigation
  const [accordionDataMap, setAccordionDataMap] = useState({});
  const [isListingDataLoaded, setIsListingDataLoaded] = useState(false);

  const listingIdUuid = currentListing.id?.uuid;
  const accordionData = accordionDataMap[listingIdUuid] || {};
  const [showApproveButton, setShowApproveButton] = useState(false);

  // Track which listing IDs have already been fetched
  const fetchedAccordionDataRef = useRef(new Set());
  const [dataLoadAttempts, setDataLoadAttempts] = useState(0);
  const maxLoadAttempts = 3;

  // Load listing data on mount with retry mechanism
  useEffect(() => {
    if (params && params.id && config && props.onLoadData) {
      props.onLoadData(params, location.search, config);
    }
  }, [
    params?.id,
    config?.layout?.listingImage,
    location.search,
    props.onLoadData,
  ]);

  // Retry data loading if listing is not loaded after initial attempt
  useEffect(() => {
    if (
      params?.id &&
      config &&
      props.onLoadData &&
      !currentListing?.id &&
      dataLoadAttempts < maxLoadAttempts
    ) {
      const retryTimeout = setTimeout(() => {
        props.onLoadData(params, location.search, config);
        setDataLoadAttempts(prev => prev + 1);
      }, 500 * (dataLoadAttempts + 1)); // Exponential backoff

      return () => clearTimeout(retryTimeout);
    }
  }, [
    params?.id,
    config,
    props.onLoadData,
    currentListing?.id,
    dataLoadAttempts,
    location.search,
  ]);

  // Track when listing data becomes available
  useEffect(() => {
    if (
      currentListing?.id &&
      currentListing?.attributes &&
      currentListing?.images &&
      currentListing.images.length > 0
    ) {
      setIsListingDataLoaded(true);
      setDataLoadAttempts(0); // Reset retry attempts when data loads successfully
    } else {
      setIsListingDataLoaded(false);
    }
  }, [
    currentListing?.id?.uuid,
    currentListing?.attributes?.title,
    currentListing?.images?.length,
    currentListing?.attributes?.publicData?.uiProductID,
  ]);

  // Single useEffect to handle all listing-dependent logic
  useEffect(() => {
    if (!currentListing?.id || !isListingDataLoaded) return;

    const listingStatus = currentListing.attributes?.publicData?.listingStatus;
    const uiProductID = currentListing.attributes?.publicData?.uiProductID;
    const uiBrandId = currentListing.attributes?.publicData?.uiBrandId;
    const uiDesignerId = currentListing.attributes?.publicData?.uiDesignerId;

    // Update approve button
    if (listingStatus !== undefined) {
      setShowApproveButton(
        listingStatus === LISTING_STATUS.SELLER_APPROVAL_PENDING ||
          listingStatus === LISTING_STATUS.READY
      );
    }

    // Fetch accordion data, keyed by listing ID to avoid cross-listing state
    if (!fetchedAccordionDataRef.current.has(listingIdUuid)) {
      const url = uiProductID
        ? `${apiBaseUrl()}/api/listing-accordion-data?productId=${uiProductID}`
        : uiBrandId && uiDesignerId
        ? `${apiBaseUrl()}/api/listing-accordion-data?brandId=${uiBrandId}&designerId=${uiDesignerId}`
        : null;

      if (url) {
        fetchedAccordionDataRef.current.add(listingIdUuid);
        fetch(url, { method: 'GET', headers: { 'Content-Type': 'application/json' } })
          .then(response => {
            if (response.ok) return response.json();
            console.error('Failed to fetch accordion data');
          })
          .then(data => {
            if (data && typeof data === 'object') {
              data.productDescription =
                currentListing.attributes?.publicData?.productDescription;
              setAccordionDataMap(prev => ({ ...prev, [listingIdUuid]: data }));
            }
          })
          .catch(error => {
            console.error('Error fetching accordion data:', error);
            fetchedAccordionDataRef.current.delete(listingIdUuid);
          });
      }
    }
  }, [
    listingIdUuid,
    currentListing?.attributes?.publicData?.uiProductID,
    currentListing?.attributes?.publicData?.uiBrandId,
    currentListing?.attributes?.publicData?.uiDesignerId,
    isListingDataLoaded,
    currentListing?.id?.uuid,
  ]);

  const [isMobileLayout, setIsMobileLayout] = useState(true);

  useEffect(() => {
    const checkIsMobile = () => {
      if (typeof window !== 'undefined' && window.matchMedia) {
        setIsMobileLayout(window.matchMedia('(max-width: 1024px)').matches);
      }
    };

    // Check on mount
    checkIsMobile();

    // Listen for viewport changes
    if (typeof window !== 'undefined' && window.matchMedia) {
      const mediaQuery = window.matchMedia('(max-width: 1024px)');
      mediaQuery.addEventListener('change', checkIsMobile);

      return () => {
        mediaQuery.removeEventListener('change', checkIsMobile);
      };
    }
  }, []);

  const PriceMaybe = props => {
    const {
      price,
      publicData,
      intl,
      marketplaceCurrency,
      showCurrencyMismatch = false,
    } = props;
    const { unitType } = publicData || {};

    // Get formatted price or currency code if the currency does not match with marketplace currency
    const { formattedPrice, priceTitle } = priceData(
      price,
      marketplaceCurrency,
      intl
    );
    // TODO: In CTA, we don't have space to show proper error message for a mismatch of marketplace currency
    //       Instead, we show the currency code in place of the price
    return showCurrencyMismatch ? (
      <div className={css.priceContainerInCTA}>
        <div className={css.priceValue} title={priceTitle}>
          {formattedPrice}
        </div>
        <div className={css.perUnitInCTA}>
          <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        </div>
      </div>
    ) : (
      <div className={css.priceContainer}>
        <p className={css.price}>{formatMoney(intl, price)}</p>
        <div className={css.perUnit}>
          <FormattedMessage id="OrderPanel.perUnit" values={{ unitType }} />
        </div>
      </div>
    );
  };

  const hasTrackedRef = useRef(false);
  useEffect(() => {
    if (!listingIdUuid || hasTrackedRef.current) {
      return;
    }

    trackProductView(currentListing);
    hasTrackedRef.current = true;
  }, [listingIdUuid]);

  const isApproved =
    currentListing.id &&
    currentListing.attributes.state !== LISTING_STATE_PENDING_APPROVAL;

  const pendingIsApproved = isPendingApprovalVariant && isApproved;

  const isReady =
    currentListing.attributes?.publicData?.listingStatus ===
    LISTING_STATUS.READY;

  const isLive =
    currentListing.attributes?.publicData?.listingStatus == LISTING_STATUS.LIVE;

  // If a /pending-approval URL is shared, the UI requires
  // authentication and attempts to fetch the listing from own
  // listings. This will fail with 403 Forbidden if the author is
  // another user. We use this information to try to fetch the
  // public listing.
  const pendingOtherUsersListing =
    (isPendingApprovalVariant || isDraftVariant) &&
    showListingError &&
    showListingError.status === 403;
  const shouldShowPublicListingPage =
    pendingIsApproved || pendingOtherUsersListing;

  if (shouldShowPublicListingPage) {
    return (
      <NamedRedirect
        name="ListingPage"
        params={params}
        search={location.search}
      />
    );
  }

  const topbar = <TopbarSpacer />;

  if (showListingError && showListingError.status === 404) {
    // 404 listing not found
    return <NotFoundPage staticContext={props.staticContext} />;
  } else if (showListingError) {
    // Other error in fetching listing
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  } else if (!currentListing.id) {
    return (
      <LoadingPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
      />
    );
  }

  const {
    description = '',
    geolocation = null,
    price = null,
    title = '',
    publicData = {},
    metadata = {},
  } = currentListing.attributes;

  const richTitle = (
    <span>
      {richText(title, {
        longWordMinLength: MIN_LENGTH_FOR_LONG_WORDS_IN_TITLE,
        longWordClass: css.longWord,
      })}
    </span>
  );

  const authorAvailable = currentListing && currentListing.author;
  const userAndListingAuthorAvailable = !!(currentUser && authorAvailable);
  const isOwnListing =
    userAndListingAuthorAvailable &&
    currentListing.author.id.uuid === currentUser.id.uuid;

  const { listingType, transactionProcessAlias, unitType } = publicData;
  if (!(listingType && transactionProcessAlias && unitType)) {
    // Listing should always contain listingType, transactionProcessAlias and unitType)
    return (
      <ErrorPage
        topbar={topbar}
        scrollingDisabled={scrollingDisabled}
        intl={intl}
        invalidListing
      />
    );
  }
  const processName = resolveLatestProcessName(
    transactionProcessAlias.split('/')[0]
  );
  const isBooking = isBookingProcess(processName);
  const isPurchase = isPurchaseProcess(processName);
  const processType = isBooking
    ? 'booking'
    : isPurchase
    ? 'purchase'
    : 'inquiry';

  const currentAuthor = authorAvailable ? currentListing.author : null;
  const ensuredAuthor = ensureUser(currentAuthor);
  const noPayoutDetailsSetWithOwnListing =
    isOwnListing &&
    processType !== 'inquiry' &&
    !currentUser?.attributes?.stripeConnected;
  const payoutDetailsWarning = noPayoutDetailsSetWithOwnListing ? (
    <span className={css.payoutDetailsWarning}>
      <FormattedMessage
        id="ListingPage.payoutDetailsWarning"
        values={{ processType }}
      />
      <NamedLink name="StripePayoutPage">
        <FormattedMessage id="ListingPage.payoutDetailsWarningLink" />
      </NamedLink>
    </span>
  ) : null;

  // When user is banned or deleted the listing is also deleted.
  // Because listing can be never showed with banned or deleted user we don't have to provide
  // banned or deleted display names for the function
  const authorDisplayName = userDisplayNameAsString(ensuredAuthor, '');

  const { formattedPrice } = priceData(price, config.currency, intl);

  const commonParams = { params, history, routes: routeConfiguration };
  const onContactUser = handleContactUser({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    location,
    setInitialValues,
    setInquiryModalOpen,
  });

  const onSubmit = handleSubmit({
    ...commonParams,
    currentUser,
    callSetInitialValues,
    getListing,
    onInitializeCardPaymentData,
  });

  const handleOrderSubmit = values => {
    const isCurrentlyClosed =
      currentListing.attributes.state === LISTING_STATE_CLOSED;
    if (isOwnListing || isCurrentlyClosed) {
      window.scrollTo(0, 0);
    } else {
      onSubmit(values);
    }
  };

  const handleApproveListingClick = () => {
    props.onRequestUpdateListingStatus(currentListing.id, LISTING_STATUS.READY);
    setShowApproveButton(false);
    currentListing.attributes.publicData.listingStatus = LISTING_STATUS.READY;
  };

  const facebookImages = currentListing?.images
    ? listingImages(currentListing, 'facebook')
    : [];
  const twitterImages = currentListing?.images
    ? listingImages(currentListing, 'twitter')
    : [];
  const schemaImages = currentListing?.images
    ? listingImages(
        currentListing,
        `${config.layout.listingImage.variantPrefix}-2x`
      ).map(img => img.url)
    : [];
  const marketplaceName = config.marketplaceName;
  const schemaTitle = intl.formatMessage(
    { id: 'ListingPage.schemaTitle' },
    { title, price: formattedPrice, marketplaceName }
  );
  // You could add reviews, sku, etc. into page schema
  // Read more about product schema
  // https://developers.google.com/search/docs/advanced/structured-data/product
  const productURL = `${config.marketplaceRootURL}${location.pathname}${location.search}${location.hash}`;
  const { designer, brand, productDescription } = publicData;
  const descBody = productDescription || description || '';
  const descSnippet = descBody.length > 130 ? descBody.substring(0, 127) + '...' : descBody;
  const metaDescription = [schemaTitle, descSnippet, 'Available on Used Icons — Stockholm, Sweden.'].filter(Boolean).join('. ');
  const schemaBrandMaybe = designer || brand
    ? { brand: { '@type': 'Brand', name: designer || brand } }
    : {};

  const schemaPriceMaybe = price
    ? {
        price: parseFloat(convertMoneyToNumber(price).toFixed(2)),
        priceCurrency: price.currency,
        shippingDetails: {
          '@type': 'OfferShippingDetails',
          shippingRate: { '@type': 'MonetaryAmount', currency: price.currency },
          shippingDestination: { '@type': 'DefinedRegion', addressCountry: 'SE' },
          deliveryTime: { '@type': 'ShippingDeliveryTime', businessDays: { '@type': 'OpeningHoursSpecification', dayOfWeek: ['Monday','Tuesday','Wednesday','Thursday','Friday'] }, handlingTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 3, unitCode: 'DAY' }, transitTime: { '@type': 'QuantitativeValue', minValue: 1, maxValue: 5, unitCode: 'DAY' } },
        },
        hasMerchantReturnPolicy: {
          '@type': 'MerchantReturnPolicy',
          applicableCountry: 'SE',
          returnPolicyCategory: 'https://schema.org/MerchantReturnNotPermitted',
        },
      }
    : {};
  const currentStock = currentListing.currentStock?.attributes?.quantity || 0;
  const isSold = currentStock === 0;
  const isDrop = currentListing.attributes.metadata?.isInDrop;
  const schemaAvailability = !currentListing.currentStock
    ? null
    : currentStock > 0
    ? 'https://schema.org/InStock'
    : 'https://schema.org/OutOfStock';

  const availabilityMaybe = schemaAvailability
    ? { availability: schemaAvailability }
    : {};

  const mappedAccordionData = [
    'productDescription',
    'brandDescription',
    'designerDescription',
  ]
    .filter(key =>
      key === 'productDescription'
        ? currentListing.attributes.publicData?.productDescription ||
          accordionData[key]
        : accordionData[key]
    )
    .map(key => {
      if (key === 'productDescription') {
        if (currentListing.attributes.publicData?.productDescription) {
          return {
            title: 'Product Description:',
            content: (
              <div>
                {currentListing.attributes.publicData.productDescription}
              </div>
            ),
          };
        }
        return {
          title: 'Product Description:',
          content: accordionData[key],
        };
      }
      return {
        title: key === 'brandDescription' ? 'Brand:' : 'Designer:',
        content: accordionData[key],
      };
    });

  const productInfoAccordionData = [
    {
      title: 'Product Info:',
      content: (
        <ul className={css.additionalData}>
          {currentListing.attributes.publicData?.additionalData?.map(
            ({ key, value }) => (
              <li key={key}>
                {key.length === 0 ? <br /> : `${key}: ${value}`}
              </li>
            )
          )}
        </ul>
      ),
    },
  ];

  const onToggleFavorites = handleToggleFavorites({
    ...commonParams,
    currentUser,
    onUpdateFavorites,
    location,
    currentListing,
  });

  return (
    <Page
      title={schemaTitle}
      scrollingDisabled={scrollingDisabled}
      author={authorDisplayName}
      description={description}
      facebookImages={facebookImages}
      twitterImages={twitterImages}
      schema={{
        '@context': 'http://schema.org',
        '@type': 'Product',
        description: metaDescription,
        name: schemaTitle,
        image: schemaImages,
        ...schemaBrandMaybe,
        offers: {
          '@type': 'Offer',
          url: productURL,
          ...schemaPriceMaybe,
          ...availabilityMaybe,
        },
      }}
    >
      <LayoutSingleColumn
        className={css.pageRoot}
        topbar={<>{topbar}</>}
        footer={<FooterContainer />}
      >
        <div className={css.breadcrumbWrapper}>
          <Breadcrumb items={buildBreadcrumbItems(location.state, title)} />
        </div>
        <div className={css.contentWrapperForProductLayout}>
          <div className={css.mainColumnForProductLayout}>
            {showListingInProgress ? (
              <div className={css.galleryPlaceholder} />
            ) : currentListing?.images?.length > 0 ? (
              <SectionGallery
                listing={currentListing}
                variantPrefix={config.layout.listingImage.variantPrefix}
                onToggleFavorites={onToggleFavorites}
                currentUser={currentUser}
              />
            ) : (
              <div className={css.loadingPlaceholder}>
                <FormattedMessage
                  id="ListingPage.noImages"
                  defaultMessage="No images available"
                />
              </div>
            )}
            <div className={css.mobileHeading}>
              <div className={css.orderPanelTitle}>
                <FormattedMessage
                  id="ListingPage.orderTitle"
                  className={css.mobileListingTitle}
                  values={{ title: richTitle }}
                />

                {isSold ? (
                  <span className={css.price}>SOLD</span>
                ) : (
                  <PriceMaybe
                    price={currentListing.attributes.price}
                    publicData={currentListing.attributes.publicData}
                    intl={intl}
                    marketplaceCurrency={config.currency}
                  />
                )}
              </div>
              {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
                <ActionBarMaybe
                  className={css.actionBarForProductLayout}
                  isOwnListing={isOwnListing}
                  listing={currentListing}
                  showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
                />
              ) : null}
              {/* {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null} */}
            </div>

            {/* <SectionTextMaybe text={description} showAsIngress /> */}

            {!isMobileLayout && (
              <div className={css.additionalAccordionWrapper}>
                <AccordionGroup accordions={mappedAccordionData} />
              </div>
            )}

            {/* <CustomListingFields
              publicData={publicData}
              metadata={metadata}
              listingFieldConfigs={listingConfig.listingFields}
              categoryConfiguration={config.categoryConfiguration}
              intl={intl}
            /> */}

            {/* <SectionMapMaybe
              geolocation={geolocation}
              publicData={publicData}
              listingId={currentListing.id}
              mapsConfig={config.maps}
            /> */}
            {/* {reviews.length > 0 && (
              <SectionReviews reviews={reviews} fetchReviewsError={fetchReviewsError} />
            )} */}
            {/* <SectionAuthorMaybe
              title={title}
              listing={currentListing}
              authorDisplayName={authorDisplayName}
              onContactUser={onContactUser}
              isInquiryModalOpen={isAuthenticated && inquiryModalOpen}
              onCloseInquiryModal={() => setInquiryModalOpen(false)}
              sendInquiryError={sendInquiryError}
              sendInquiryInProgress={sendInquiryInProgress}
              onSubmitInquiry={onSubmitInquiry}
              currentUser={currentUser}
              onManageDisableScrolling={onManageDisableScrolling}
            /> */}
          </div>
          <div className={css.orderColumnForProductLayout}>
            <OrderPanel
              className={css.productOrderPanel}
              listing={currentListing}
              isOwnListing={isOwnListing}
              onSubmit={handleOrderSubmit}
              authorLink={
                <NamedLink
                  className={css.authorNameLink}
                  name="ListingPage"
                  params={params}
                  to={{ hash: '#author' }}
                >
                  {authorDisplayName}
                </NamedLink>
              }
              title={
                <FormattedMessage
                  id="ListingPage.orderTitle"
                  values={{ title: richTitle }}
                />
              }
              titleDesktop={
                <div className={css.orderPanelTitle}>
                  <FormattedMessage
                    id="ListingPage.orderTitle"
                    values={{ title: richTitle }}
                  />
                </div>
              }
              payoutDetailsWarning={payoutDetailsWarning}
              author={ensuredAuthor}
              onManageDisableScrolling={onManageDisableScrolling}
              onContactUser={onContactUser}
              monthlyTimeSlots={monthlyTimeSlots}
              onFetchTimeSlots={onFetchTimeSlots}
              onFetchTransactionLineItems={onFetchTransactionLineItems}
              lineItems={lineItems}
              fetchLineItemsInProgress={fetchLineItemsInProgress}
              fetchLineItemsError={fetchLineItemsError}
              validListingTypes={config.listing.listingTypes}
              marketplaceCurrency={config.currency}
              dayCountAvailableForBooking={
                config.stripe.dayCountAvailableForBooking
              }
              marketplaceName={config.marketplaceName}
              onToggleFavorites={onToggleFavorites}
              currentUser={currentUser}
            />

            {isOwnListing && (isReady || isLive) && (
              <div className={css.approveContainer}>
                <PrimaryButtonInline className={css.approveButton} disabled>
                  This is your own listing.
                </PrimaryButtonInline>
                <p
                  style={{
                    textAlign: 'center',
                    fontSize: '12px',
                    paddingBottom: '10px',
                    cursor: 'pointer',
                  }}
                  onClick={() => history.push(`/listings?messages=${currentListing.id.uuid}`)}
                >
                  Not happy? Click here to get in touch.
                </p>
              </div>
            )}

            {isMobileLayout ? (
              <div className={css.additionalAccordionWrapper}>
                <AccordionGroup
                  accordions={[
                    ...productInfoAccordionData,
                    ...mappedAccordionData,
                  ]}
                />
              </div>
            ) : (
              <AccordionGroup
                className={css.productInfoAccordion}
                accordions={productInfoAccordionData}
              />
            )}

            {currentListing.id && noPayoutDetailsSetWithOwnListing ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                showNoPayoutDetailsSet={noPayoutDetailsSetWithOwnListing}
              />
            ) : null}
            {/* {currentListing.id ? (
              <ActionBarMaybe
                className={css.actionBarForProductLayout}
                isOwnListing={isOwnListing}
                listing={currentListing}
                editParams={{
                  id: listingId.uuid,
                  slug: listingSlug,
                  type: listingPathParamType,
                  tab: listingTab,
                }}
              />
            ) : null} */}
          </div>
        </div>

        <SellerMessagesDrawer
          isOpen={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          listingTitle={currentListing.attributes?.title}
          messages={
            currentListing.attributes?.privateData?.sellerMessages?.messages ||
            []
          }
          sendMessageInProgress={sendSellerMessageInProgress}
          onSendMessage={text =>
            onSendSellerMessage(
              currentListing.id,
              text,
              currentListing.attributes?.privateData?.sellerMessages
            )
          }
        />

        {/* Similar products component - added outside the main content layout */}
        {isLive && currentListing && currentListing.id && (
          <div className={css.similarProductsContainer}>
            <SimilarProducts
              currentListing={currentListing}
              currentUser={currentUser}
              onToggleFavorites={onToggleFavorites}
            />
          </div>
        )}
      </LayoutSingleColumn>
    </Page>
  );
};

ListingPageComponent.defaultProps = {
  currentUser: null,
  inquiryModalOpenForListingId: null,
  showListingError: null,
  reviews: [],
  fetchReviewsError: null,
  monthlyTimeSlots: null,
  sendInquiryError: null,
  lineItems: null,
  fetchLineItemsError: null,
};

ListingPageComponent.propTypes = {
  // from useHistory
  history: shape({
    push: func.isRequired,
  }).isRequired,
  // from useLocation
  location: shape({
    search: string,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,
  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,

  params: shape({
    id: string.isRequired,
    slug: string,
    variant: oneOf([
      LISTING_PAGE_DRAFT_VARIANT,
      LISTING_PAGE_PENDING_APPROVAL_VARIANT,
    ]),
  }).isRequired,

  isAuthenticated: bool.isRequired,
  currentUser: propTypes.currentUser,
  getListing: func.isRequired,
  getOwnListing: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  scrollingDisabled: bool.isRequired,
  inquiryModalOpenForListingId: string,
  showListingError: propTypes.error,
  callSetInitialValues: func.isRequired,
  reviews: arrayOf(propTypes.review),
  fetchReviewsError: propTypes.error,
  monthlyTimeSlots: object,
  // monthlyTimeSlots could be something like:
  // monthlyTimeSlots: {
  //   '2019-11': {
  //     timeSlots: [],
  //     fetchTimeSlotsInProgress: false,
  //     fetchTimeSlotsError: null,
  //   }
  // }
  sendInquiryInProgress: bool.isRequired,
  sendInquiryError: propTypes.error,
  onSendInquiry: func.isRequired,
  onInitializeCardPaymentData: func.isRequired,
  onFetchTransactionLineItems: func.isRequired,
  lineItems: array,
  fetchLineItemsInProgress: bool.isRequired,
  fetchLineItemsError: propTypes.error,
  onLoadData: func.isRequired,
};

const EnhancedListingPage = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const history = useHistory();
  const location = useLocation();

  const showListingError = props.showListingError;
  const isVariant = props.params?.variant?.length > 0;
  if (isForbiddenError(showListingError) && !isVariant) {
    // This can happen if private marketplace mode is active
    return (
      <NamedRedirect
        name="SignupPage"
        state={{
          from: `${location.pathname}${location.search}${location.hash}`,
        }}
      />
    );
  }

  const currentUser = props.currentUser;
  const isPrivateMarketplace =
    config.accessControl.marketplace.private === true;
  const isUnauthorizedUser = currentUser && !isUserAuthorized(currentUser);
  const hasUserPendingApprovalError = isErrorUserPendingApproval(
    showListingError
  );
  if (
    (isPrivateMarketplace && isUnauthorizedUser) ||
    hasUserPendingApprovalError
  ) {
    return (
      <NamedRedirect
        name="NoAccessPage"
        params={{ missingAccessRight: NO_ACCESS_PAGE_USER_PENDING_APPROVAL }}
      />
    );
  }

  return (
    <ListingPageComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      history={history}
      location={location}
      {...props}
    />
  );
};

const mapStateToProps = state => {
  const { isAuthenticated } = state.auth;
  const {
    showListingInProgress,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    sendInquiryInProgress,
    sendInquiryError,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    inquiryModalOpenForListingId,
    sendSellerMessageInProgress,
  } = state.ListingPage;
  const { currentUser } = state.user;

  const getListing = id => {
    const ref = { id, type: 'listing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  const getOwnListing = id => {
    const ref = { id, type: 'ownListing' };
    const listings = getMarketplaceEntities(state, [ref]);
    return listings.length === 1 ? listings[0] : null;
  };

  return {
    isAuthenticated,
    currentUser,
    getListing,
    getOwnListing,
    scrollingDisabled: isScrollingDisabled(state),
    inquiryModalOpenForListingId,
    showListingInProgress,
    showListingError,
    reviews,
    fetchReviewsError,
    monthlyTimeSlots,
    lineItems,
    fetchLineItemsInProgress,
    fetchLineItemsError,
    sendInquiryInProgress,
    sendInquiryError,
    sendSellerMessageInProgress,
    requestUpdateListingStatus,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  callSetInitialValues: (setInitialValues, values, saveToSessionStorage) =>
    dispatch(setInitialValues(values, saveToSessionStorage)),
  onFetchTransactionLineItems: params =>
    dispatch(fetchTransactionLineItems(params)),
  onSendInquiry: (listing, message) => dispatch(sendInquiry(listing, message)),
  onInitializeCardPaymentData: () => dispatch(initializeCardPaymentData()),
  onFetchTimeSlots: (listingId, start, end, timeZone) =>
    dispatch(fetchTimeSlots(listingId, start, end, timeZone)),
  onUpdateFavorites: payload => dispatch(updateProfile(payload)), // add this row
  onRequestUpdateListingStatus: (listingId, newStatus) => {
    dispatch(requestUpdateListingStatus(listingId, newStatus));
  },
  onSendSellerMessage: (listingId, message, existingMessages) =>
    dispatch(sendSellerMessage(listingId, message, existingMessages)),
  onMarkSellerMessagesRead: (listingId, existingSellerMessages) =>
    dispatch(markSellerMessagesRead(listingId, existingSellerMessages)),
  onLoadData: (params, search, config) =>
    dispatch(loadData(params, search, config)),
});

// Note: it is important that the withRouter HOC is **outside** the
// connect HOC, otherwise React Router won't rerender any Route
// components since connect implements a shouldComponentUpdate
// lifecycle hook.
//
// See: https://github.com/ReactTraining/react-router/issues/4671
const ListingPage = compose(connect(mapStateToProps, mapDispatchToProps))(
  EnhancedListingPage
);

export default ListingPage;
